import type { ReactNode } from 'react';
import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { initials } from '../lib/text';
import { StatusChip } from './StatusChip';
import { TabStrip } from './TabStrip';
import type { Tone } from './tone';

type Props<T extends string> = {
  back: { to: string; label: string };
  name: string;
  status: { label: string; tone: Tone };
  details: string[];
  actions: ReactNode;
  /** the tab strip under the header */
  tabs: readonly T[];
  tab: T;
  onTab: (tab: T) => void;
};

/** The top of a company's page: "← back", the round initials, the name with its status, a line of details, the buttons, and the tabs */
export function ProfileHeader<T extends string>({ back, name, status, details, actions, tabs, tab, onTab }: Props<T>) {
  return (
    <Stack spacing={{ xs: 1.5, md: 2 }}>
      <Button
        component={RouterLink}
        to={back.to}
        startIcon={<ArrowBack />}
        size="small"
        sx={{ alignSelf: 'flex-start' }}
      >
        {back.label}
      </Button>
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.5, md: 2 },
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) auto' },
          alignItems: 'start',
        }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Avatar sx={{ width: 64, height: 64, fontSize: 22, bgcolor: 'primary.main' }}>{initials(name)}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="h4" component="h1">
                {name}
              </Typography>
              <StatusChip label={status.label} tone={status.tone} />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 0.5, color: 'text.secondary' }}>
              {details.filter(Boolean).map((t) => (
                <Typography key={t} variant="body2">
                  {t}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {actions}
        </Stack>
      </Box>
      <TabStrip tabs={tabs} value={tab} onChange={onTab} />
    </Stack>
  );
}
